import React, { useState, useEffect } from 'react';
import Avatar from '@mui/material/Avatar';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Alert, Box, Container, Grid, Paper, Stack, Typography } from '@mui/material';
import { NavLink, useNavigate } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import axios from '../../../axios';
import { useAuth } from '../../../context/AuthContext';
import Swal from 'sweetalert2';
import PasswordTextField from '../../PasswordTextField';
import { notifyError, notifySuccess, getApiErrorMessage } from '../../../utils/notify';
import { PASSWORD_PATTERN, PASSWORD_HINT } from '../../../utils/validation';
import './Account.scss';

// Ta sama reguła hasła co u właściciela konta (Account.js)
const validationChangePasswordSchema = Yup.object({
  oldPassword: Yup.string().required('Obecne hasło jest wymagane'),
  newPassword: Yup.string()
    .matches(PASSWORD_PATTERN, `Hasło musi mieć ${PASSWORD_HINT}`)
    .required('Nowe hasło jest wymagane'),
  confirmNewPassword: Yup.string()
    .oneOf([Yup.ref('newPassword'), null], 'Hasła muszą być identyczne')
    .required('Powtórz nowe hasło'),
});

export default function AccountEmployee() {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [accountInfo, setAccountInfo] = useState({
    funeralhomename: '',
    username: '',
    email: '',
    download_count: '',
  });
  const { isLoggedIn, isTrial, isOwner} = useAuth();

  useEffect(() => {
    if (!isLoggedIn) {
        navigate('/login');
    } else {
      fetchUserData();
    }

    if(isOwner) {
      navigate("/account");
    }
}, []);

async function fetchUserData() {
    try {
        const res = await axios.get('/user');
          setAccountInfo(res.data[0]);
    } catch (error) {
        notifyError(getApiErrorMessage(error, 'Nie udało się pobrać danych konta.'));
    }
}

// Zwraca true po udanym zapisie – dopiero wtedy wychodzimy z trybu edycji
async function handleSaving(accountInfo) {
  try {
    await axios.put('/updateUser',
      {
        username: accountInfo.username,
        email: accountInfo.email,
      }
    );
    notifySuccess('Dane zostały zapisane.');
    return true;
  } catch (error) {
    notifyError(getApiErrorMessage(error, 'Nie udało się zapisać danych.'));
    return false;
  }
}

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAccountInfo({
      ...accountInfo,
      [name]: value
    });
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSaveClick = async () => {
    setIsSaving(true);
    const saved = await handleSaving(accountInfo);
    setIsSaving(false);
    if (saved) {
      setIsEditing(false);
    }
  };

  // Anulowanie edycji przywraca dane zapisane na serwerze
  const handleCancelEditClick = () => {
    setIsEditing(false);
    fetchUserData();
  };

  const handlePasswordChangeClick = () => {
    setIsPasswordDialogOpen(true);
  };


const formatDate = (timestamp) => {
  if (!timestamp) {
    return 'Brak danych';
  }
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return 'Brak danych';
  }
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Miesiące są indeksowane od 0
  const year = date.getFullYear();
  return `${day}.${month}.${year} r.`;
};

  const avatarInitial = accountInfo.email ? accountInfo.email.charAt(0).toUpperCase() : '?';

  // Kolory i kształty jak na stronie głównej (src/components/_brand.scss);
  // przyciski główne (granatowe pigułki) i .app-btn-outline mają style globalne w index.css
  const primaryButtonStyles = {
    textTransform: 'none',
    fontWeight: 600,
    px: 3,
  };

  const outlinedButtonStyles = {
    textTransform: 'none',
    fontWeight: 600,
    px: 3,
  };

  const cardStyles = {
    p: { xs: 3, md: 4 },
    borderRadius: '16px',
    border: '1px solid #dbe4ec',
    boxShadow: '0 20px 40px rgba(28, 63, 88, 0.06)',
    backgroundColor: 'var(--surface)',
  };

  const cardTitleStyles = {
    fontFamily: "Georgia, 'Times New Roman', serif",
    fontWeight: 400,
    color: 'var(--brand-navy)',
    fontSize: { xs: '22px', md: '24px' },
  };

  return (
    <>
      <Box
        className="accountModern"
        sx={{
          background: 'var(--brand-soft)',
          minHeight: '100vh',
          // ten sam odstęp od menu co na pozostałych stronach panelu (_panelLayout.scss)
          pt: { xs: '96px', md: '128px' },
          pb: { xs: '48px', md: '64px' },
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={4}>
            <Box className="accountModern_hero">
              <Typography variant="overline" className="accountModern_eyebrow">
                Panel konta
              </Typography>
              <Typography variant="h4" component="h1" className="accountModern_title">
                Moje konto
              </Typography>
              <Typography variant="body2" className="accountModern_subtitle">
                Twoje dane w panelu zakładu oraz informacje używane podczas generowania materiałów.
              </Typography>
            </Box>
            {isTrial && (
              <Alert
                severity="info"
                sx={{
                  borderRadius: '12px',
                  border: '1px solid #dbe4ec',
                  borderLeft: '4px solid #1c3f58',
                  backgroundColor: 'var(--surface-2)',
                  color: 'var(--brand-navy)',
                  '& .MuiAlert-icon': { color: 'var(--brand-navy)' },
                }}
              >
                <Typography component="span" fontWeight={700}>
                  Korzystasz z okresu próbnego
                </Typography>{' '}
                – Twoje pliki będą miały znak wodny.
              </Alert>
            )}

            {/* Box: Stack zeruje marginesy dzieci, a Grid potrzebuje ujemnego marginesu do wyrównania krawędzi */}
            <Box>
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Paper sx={cardStyles}>
                  <Stack spacing={2} alignItems="center" textAlign="center">
                    <Avatar sx={{ width: 88, height: 88, bgcolor: 'var(--brand-navy-fill)', fontSize: '2rem', fontFamily: "Georgia, 'Times New Roman', serif" }}>{avatarInitial}</Avatar>
                    <Typography variant="h5" sx={{ ...cardTitleStyles, overflowWrap: 'anywhere' }}>
                      {accountInfo.email}
                    </Typography>
                  </Stack>

                  <Stack spacing={2} mt={4}>
                    <TextField
                      label="Nazwa zakładu pogrzebowego"
                      name="funeralhomename"
                      value={accountInfo.funeralhomename}
                      fullWidth
                      disabled
                    />
                    <TextField
                      label="Nazwa użytkownika"
                      name="username"
                      value={accountInfo.username}
                      onChange={handleInputChange}
                      fullWidth
                      disabled={!isEditing}
                    />
                    <TextField
                      label="E-mail"
                      name="email"
                      value={accountInfo.email}
                      fullWidth
                      disabled
                    />
                    <TextField
                      label="Liczba pobrań"
                      name="download_count"
                      value={accountInfo.download_count}
                      fullWidth
                      disabled
                    />
                  </Stack>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={4}>
                    <Button
                      variant="outlined"
                      className="app-btn-outline"
                      onClick={handlePasswordChangeClick}
                      sx={{ ...outlinedButtonStyles, width: { xs: '100%', sm: 'auto' } }}
                    >
                      Zmień hasło
                    </Button>

                    {!isEditing ? (
                      <Button
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={handleEditClick}
                        sx={{ ...primaryButtonStyles, width: { xs: '100%', sm: 'auto' } }}
                      >
                        Edytuj
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="contained"
                          startIcon={<SaveIcon />}
                          onClick={handleSaveClick}
                          disabled={isSaving}
                          sx={{ ...primaryButtonStyles, width: { xs: '100%', sm: 'auto' } }}
                        >
                          {isSaving ? 'Zapisywanie…' : 'Zapisz zmiany'}
                        </Button>
                        <Button
                          variant="outlined"
                          className="app-btn-outline"
                          onClick={handleCancelEditClick}
                          disabled={isSaving}
                          sx={{ ...outlinedButtonStyles, width: { xs: '100%', sm: 'auto' } }}
                        >
                          Anuluj
                        </Button>
                      </>
                    )}
                  </Stack>

                  <Typography variant="body2" sx={{ mt: 3, color: 'var(--brand-muted)' }}>
                    Data wygaśnięcia subskrypcji:{' '}
                    <Box component="span" sx={{ color: 'var(--brand-navy)', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {formatDate(accountInfo.subscription_expiration_date)}
                    </Box>
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={cardStyles}>
                  <Typography variant="h5" component="h2" sx={cardTitleStyles}>
                    Personalizuj informacje do generowania
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'var(--brand-muted)', mt: 0.5 }}>
                    Słowniki i szablony podpowiadane w formularzu zmarłego.
                  </Typography>
                  <Grid container spacing={2} mt={1}>
                    <Grid item xs={12} sm={6}>
                      <Button component={NavLink} to="/massplaces" fullWidth variant="outlined" className="app-btn-outline" sx={{ ...outlinedButtonStyles, minHeight: 48 }}>
                        Edytuj miejsca mszy
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Button component={NavLink} to="/funeralplaces" fullWidth variant="outlined" className="app-btn-outline" sx={{ ...outlinedButtonStyles, minHeight: 48 }}>
                        Edytuj miejsca pogrzebu
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Button component={NavLink} to="/quotes" fullWidth variant="outlined" className="app-btn-outline" sx={{ ...outlinedButtonStyles, minHeight: 48 }}>
                        Edytuj cytaty
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Button component={NavLink} to="/definitions" fullWidth variant="outlined" className="app-btn-outline" sx={{ ...outlinedButtonStyles, minHeight: 48 }}>
                        Edytuj określenia osoby zmarłej
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Button component={NavLink} to="/familymembers" fullWidth variant="outlined" className="app-btn-outline" sx={{ ...outlinedButtonStyles, minHeight: 48 }}>
                        Edytuj powiadamiających
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Button component={NavLink} to="/templates" fullWidth variant="outlined" className="app-btn-outline" sx={{ ...outlinedButtonStyles, minHeight: 48 }}>
                        Edytuj szablony
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
            </Box>
          </Stack>
        </Container>
      </Box>

      <Dialog open={isPasswordDialogOpen} onClose={() => setIsPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Zmień hasło</DialogTitle>
        <Formik
          initialValues={{ oldPassword: '', newPassword: '', confirmNewPassword: '' }}
          validationSchema={validationChangePasswordSchema}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              await axios.post('/changePassword', {
                oldPassword: values.oldPassword,
                newPassword: values.newPassword
              });
              // Po zmianie hasła serwer unieważnia token – trzeba zalogować się ponownie
              await Swal.fire({
                title: 'Sukces!',
                text: 'Hasło zmienione. Zaloguj się ponownie.',
                icon: 'success',
                confirmButtonText: 'OK',
                background: 'var(--surface)',
                color: '#155724',
                confirmButtonColor: '#28a745'
              });
              localStorage.removeItem('token');
              setIsPasswordDialogOpen(false);
              navigate('/login');
            } catch (error) {
              Swal.fire({
                title: 'Błąd!',
                text: getApiErrorMessage(error, 'Nie udało się zmienić hasła.', { 400: 'Obecne hasło jest nieprawidłowe.' }),
                icon: 'error',
                confirmButtonText: 'OK',
                background: 'var(--surface)',
                color: '#000',
                confirmButtonColor: '#d33'
              });
            }
            setSubmitting(false);
          }}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form>
              <DialogContent>
                <Field
                  as={PasswordTextField}
                  autoFocus
                  margin="dense"
                  label="Obecne hasło"
                  fullWidth
                  variant="outlined"
                  name="oldPassword"
                  error={touched.oldPassword && Boolean(errors.oldPassword)}
                  helperText={touched.oldPassword && errors.oldPassword}
                />
                <Field
                  as={PasswordTextField}
                  margin="dense"
                  label="Nowe hasło"
                  fullWidth
                  variant="outlined"
                  name="newPassword"
                  error={touched.newPassword && Boolean(errors.newPassword)}
                  helperText={(touched.newPassword && errors.newPassword) || PASSWORD_HINT}
                />
                <Field
                  as={PasswordTextField}
                  margin="dense"
                  label="Powtórz nowe hasło"
                  fullWidth
                  variant="outlined"
                  name="confirmNewPassword"
                  error={touched.confirmNewPassword && Boolean(errors.confirmNewPassword)}
                  helperText={touched.confirmNewPassword && errors.confirmNewPassword}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setIsPasswordDialogOpen(false)} className="app-btn-outline" sx={{ px: 3 }}>
                  Anuluj
                </Button>
                <Button type="submit" color="primary" variant="contained" disabled={isSubmitting} sx={{ px: 3 }}>
                  {isSubmitting ? 'Zapisywanie…' : 'Zmień hasło'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  );
}
